import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UserOutlined, 
  TeamOutlined, 
  FileTextOutlined,
  ShoppingOutlined,
  DollarOutlined,
  SettingOutlined,
  UnorderedListOutlined,
  PlusOutlined,
  BarChartOutlined,
  UserAddOutlined,
  ShoppingCartOutlined,
  FileAddOutlined,
  CreditCardOutlined,
  ControlOutlined,
  UsergroupAddOutlined,
  SafetyOutlined,
  CalendarOutlined,
  IdcardOutlined,
  CalendarTwoTone,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CrownOutlined,
  ApartmentOutlined,
  TrophyOutlined,
  ToolOutlined,
  ScheduleOutlined,
  ThunderboltOutlined,
  SyncOutlined,
  CustomerServiceOutlined,
  FormOutlined,
  NotificationOutlined,
  MessageOutlined,
  AppstoreOutlined,
  DashboardOutlined,
  BankOutlined,
  SnippetsOutlined,
  TranslationOutlined,
  CheckSquareOutlined,
  ProjectOutlined,
  FileProtectOutlined,
  PhoneOutlined,
  StopOutlined,
} from '@ant-design/icons';
import MainLayout from '../layouts/MainLayout';
import MenuIcons from '../components/MenuIcons';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import { isLoginModuleEnabled, isLoginSubmenuAllowed } from '../utils/loginModuleMenus';
import { usePermissions } from '../contexts/PermissionsContext';
import { message } from 'antd';

const Dashboard = () => {
  const { activeMenu, setActiveMenu, closeSidebar } = useSidebar();
  const navigate = useNavigate();
  const { loginActionModule, actionToken, user } = useAuth();
  const {
    canAccessMenu,
    isModuleActive,
    filterMenuItems,
    canShowMenuItem,
    isSuperAdmin,
    isAdmin,
  } = usePermissions();

  // تعریف منوها و زیرمنوهایشان با آیکون و رنگ
  const allMenuConfig = [
    {
      key: 'dashboard-module',
      label: 'داشبورد',
      icon: <DashboardOutlined />,
      color: 'from-indigo-500 to-indigo-600',
      items: [
        {
          label: 'داشبورد خصوصی',
          icon: <DashboardOutlined />,
          path: '/dashboard/private',
          onClick: () => navigate('/dashboard/private'),
        },
        {
          label: 'داشبورد پیشرفته',
          icon: <BarChartOutlined />,
          path: '/dashboard/advanced',
          onClick: () => navigate('/dashboard/advanced'),
        },
      ],
    },
    {
      key: 'leads',
      label: 'سرنخ‌ها',
      icon: <UserOutlined />,
      color: 'from-blue-500 to-blue-600',
      module: 'leads',
      requiredPermission: 'view_lead',
      items: [
        { 
          label: 'لیست سرنخ‌ها', 
          icon: <UnorderedListOutlined />,
          path: '/leads',
          permission: 'view_lead',
          onClick: () => navigate('/leads')
        },
        { 
          label: 'سرنخ جدید', 
          icon: <PlusOutlined />,
          path: '/leads/add',
          permission: 'add_lead',
          onClick: () => navigate('/leads/add') 
        },
        { 
          label: 'گزارش', 
          icon: <BarChartOutlined />,
          path: '/leads/reports',
          permission: 'view_lead',
          onClick: () => navigate('/leads/reports') 
        },
      ],
    },
    {
      key: 'customers',
      label: 'مشتریان',
      icon: <TeamOutlined />,
      color: 'from-green-500 to-green-600',
      module: 'clients',
      requiredPermission: 'view_clients',
      items: [
        { 
          label: 'لیست مشتریان', 
          icon: <UnorderedListOutlined />,
          permission: 'view_clients',
          path: '/clients',
          onClick: () => navigate('/clients') 
        },
        { 
          label: 'مشتری جدید', 
          icon: <UserAddOutlined />,
          permission: 'add_clients',
          path: '/clients/add',
          onClick: () => navigate('/clients/add') 
        },
      ],
    },
    {
      key: 'products',
      label: 'محصولات',
      icon: <ShoppingOutlined />,
      color: 'from-purple-500 to-purple-600',
      module: 'products',
      requiredPermission: 'view_product',
      items: [
        { 
          label: 'لیست محصولات', 
          icon: <UnorderedListOutlined />,
          path: '/products',
          permission: 'view_product',
          onClick: () => navigate('/products') 
        },
        { 
          label: 'محصول جدید', 
          icon: <ShoppingCartOutlined />,
          path: '/products/add',
          permission: 'add_product',
          onClick: () => navigate('/products/add') 
        },
      ],
    },
    {
      key: 'orders',
      label: 'سفارش‌ها',
      icon: <ShoppingCartOutlined />,
      color: 'from-emerald-500 to-emerald-600',
      module: 'orders',
      requiredPermission: 'view_order',
      items: [
        {
          label: 'لیست سفارش‌ها',
          icon: <UnorderedListOutlined />,
          path: '/orders',
          permission: 'view_order',
          onClick: () => navigate('/orders')
        },
        {
          label: 'سفارش جدید',
          icon: <PlusOutlined />,
          path: '/orders/add',
          permission: 'add_order',
          onClick: () => navigate('/orders/add')
        },
      ],
    },
    {
      key: 'work',
      label: 'کار',
      icon: <ProjectOutlined />,
      color: 'from-indigo-500 to-violet-600',
      module: null,
      requiredPermission: null,
      items: [
        {
          label: 'وظایف',
          icon: <CheckSquareOutlined />,
          path: '/work/tasks',
          permission: 'view_task',
          module: 'tasks',
          onClick: () => navigate('/work/tasks'),
        },
        {
          label: 'پروژه‌ها',
          icon: <ProjectOutlined />,
          path: '/work/projects',
          permission: 'view_project',
          module: 'projects',
          onClick: () => navigate('/work/projects'),
        },
        {
          label: 'قراردادها',
          icon: <FileProtectOutlined />,
          path: '/work/contracts',
          permission: 'view_contract',
          module: 'contracts',
          onClick: () => navigate('/work/contracts'),
        },
      ],
    },
    {
      key: 'tickets',
      label: 'تیکت‌ها',
      icon: <CustomerServiceOutlined />,
      color: 'from-sky-500 to-sky-600',
      module: 'tickets',
      requiredPermission: 'view_tickets',
      items: [
        {
          label: 'لیست تیکت‌ها',
          icon: <UnorderedListOutlined />,
          path: '/tickets',
          permission: 'view_tickets',
          onClick: () => navigate('/tickets'),
        },
        {
          label: 'تیکت جدید',
          icon: <PlusOutlined />,
          path: '/tickets/add',
          permission: 'add_tickets',
          onClick: () => navigate('/tickets/add'),
        },
        {
          label: 'تنظیمات تیکت',
          icon: <SettingOutlined />,
          path: '/tickets/settings',
          permission: 'manage_ticket_setting',
          onClick: () => navigate('/tickets/settings'),
        },
        {
          label: 'فرم تیکت مشتری',
          icon: <FormOutlined />,
          path: '/tickets/ticket-form',
          permission: 'manage_ticket_setting',
          onClick: () => navigate('/tickets/ticket-form'),
        },
      ],
    },
    {
      key: 'call-center',
      label: 'مرکز تماس',
      icon: <PhoneOutlined />,
      color: 'from-teal-500 to-teal-600',
      module: 'callcenter',
      requiredPermission: 'view_callcenter',
      items: [
        {
          label: 'تماس‌ها',
          icon: <UnorderedListOutlined />,
          path: '/call-center/calls',
          permission: 'view_callcenter',
          onClick: () => navigate('/call-center/calls'),
        },
        {
          label: 'اپراتورها',
          icon: <TeamOutlined />,
          path: '/call-center/agents',
          permission: 'manage_callcenter_agents',
          onClick: () => navigate('/call-center/agents'),
        },
        {
          label: 'تنظیمات',
          icon: <SettingOutlined />,
          path: '/call-center/settings',
          permission: 'manage_callcenter_settings',
          onClick: () => navigate('/call-center/settings'),
        },
        // {
        //   label: 'لیست سیاه',
        //   icon: <StopOutlined />,
        //   path: '/call-center/blacklist',
        //   permission: 'manage_callcenter_blacklist',
        //   onClick: () => navigate('/call-center/blacklist'),
        // },
        {
          label: 'گزارش‌ها',
          icon: <BarChartOutlined />,
          path: '/call-center/reports',
          permission: 'view_callcenter_reports',
          onClick: () => navigate('/call-center/reports'),
        },
      ],
    },
    {
      key: 'notices',
      label: 'اعلانات',
      icon: <NotificationOutlined />,
      color: 'from-yellow-500 to-yellow-600',
      module: 'notices',
      requiredPermission: 'view_notice',
      items: [
        {
          label: 'لیست اعلانات',
          icon: <UnorderedListOutlined />,
          path: '/notices',
          permission: 'view_notice',
          onClick: () => navigate('/notices'),
        },
        {
          label: 'اعلان جدید',
          icon: <PlusOutlined />,
          path: '/notices/add',
          permission: 'add_notice',
          onClick: () => navigate('/notices/add'),
        },
      ],
    },
    {
      key: 'messages',
      label: 'پیام‌ها',
      icon: <MessageOutlined />,
      color: 'from-cyan-500 to-cyan-600',
      module: 'messages',
      requiredPermission: 'view_message',
      items: [
        {
          label: 'لیست پیام‌ها',
          icon: <UnorderedListOutlined />,
          path: '/messages',
          permission: 'view_message',
          onClick: () => navigate('/messages'),
        },
      ],
    },
    // {
    //   key: 'invoices',
    //   label: 'فاکتورها',
    //   icon: <FileTextOutlined />,
    //   color: 'from-orange-500 to-orange-600',
    //   module: 'invoices',
    //   requiredPermission: 'view_invoices',
    //   items: [
    //     { 
    //       label: 'لیست فاکتورها', 
    //       icon: <UnorderedListOutlined />,
    //       permission: 'view_invoices',
    //       onClick: () => console.log('لیست فاکتورها') 
    //     },
    //     { 
    //       label: 'فاکتور جدید', 
    //       icon: <FileAddOutlined />,
    //       permission: 'add_invoices',
    //       onClick: () => console.log('فاکتور جدید') 
    //     },
    //   ],
    // },
    {
      key: 'finance',
      label: 'امور مالی',
      icon: <DollarOutlined />,
      color: 'from-emerald-500 to-emerald-600',
      module: 'payments',
      requiredPermission: 'view_payments',
      items: [
        { 
          label: 'داشبورد مالی', 
          icon: <DashboardOutlined />,
          path: '/finance/dashboard',
          permission: 'view_payments',
          onClick: () => navigate('/finance/dashboard') 
        },
        { 
          label: 'حساب بانکی', 
          icon: <CreditCardOutlined />,
          path: '/finance/bank-accounts',
          permission: 'view_bankaccount',
          onClick: () => navigate('/finance/bank-accounts') 
        },
        { 
          label: 'هزینه‌ها', 
          icon: <DollarOutlined />,
          path: '/finance/expenses',
          permission: 'view_expenses',
          onClick: () => navigate('/finance/expenses') 
        },
        { 
          label: 'هزینه‌های دوره‌ای', 
          icon: <SyncOutlined />,
          path: '/finance/recurring-expenses',
          permission: 'manage_recurring_expense',
          onClick: () => navigate('/finance/recurring-expenses') 
        },
      ],
    },
    {
      key: 'calendar',
      label: 'تقویم من',
      icon: <CalendarOutlined />,
      color: 'from-pink-500 to-pink-600',
      module: 'events',
      requiredPermission: 'view_events',
      items: [
        { 
          label: 'تقویم من', 
          icon: <CalendarOutlined />,
          path: '/calendar',
          permission: 'view_events',
          onClick: () => navigate('/calendar') 
        },
      ],
    },
    {
      key: 'hr',
      label: 'منابع انسانی',
      icon: <UsergroupAddOutlined />,
      color: 'from-cyan-500 to-cyan-600',
      module: 'employees',
      // HR به چندین پرمیشن نیاز دارد - اگر حداقل یکی را داشت نمایش می‌دهد
      requiredPermissions: ['view_employees', 'view_attendance', 'view_leave'],
      items: [
        { 
          label: 'داشبورد', 
          icon: <DashboardOutlined />,
          path: '/hr/dashboard',
          onClick: () => navigate('/hr/dashboard') 
        },
        { 
          label: 'دپارتمان', 
          icon: <ApartmentOutlined />,
          path: '/hr/departments',
          permission: 'view_department',
          onClick: () => navigate('/hr/departments') 
        },
        { 
          label: 'سمت شغلی', 
          icon: <CrownOutlined />,
          path: '/hr/positions',
          permission: 'view_designation',
          onClick: () => navigate('/hr/positions') 
        },
        { 
          label: 'کارمندان', 
          icon: <IdcardOutlined />,
          path: '/hr/employees',
          permission: 'view_employees',
          onClick: () => navigate('/hr/employees') 
        },
        { 
          label: 'برنامه شیفت‌ها', 
          icon: <ClockCircleOutlined />,
          path: '/hr/shifts',
          permission: 'view_shift_roster',
          onClick: () => navigate('/hr/shifts') 
        },
        { 
          label: 'مرخصی‌ها', 
          icon: <CalendarTwoTone />,
          path: '/hr/leaves',
          permission: 'view_leave',
          onClick: () => navigate('/hr/leaves') 
        },
        { 
          label: 'حضور و غیاب', 
          icon: <CheckCircleOutlined />,
          path: '/hr/attendance',
          permission: 'view_attendance',
          onClick: () => navigate('/hr/attendance') 
        },
        { 
          label: 'تعطیلی', 
          icon: <CloseCircleOutlined />,
          path: '/hr/holidays',
          permission: 'view_holiday',
          onClick: () => navigate('/hr/holidays') 
        },
        { 
          label: 'تنظیمات مرخصی', 
          icon: <ToolOutlined />,
          path: '/hr/leaves-settings',
          permission: 'manage_leave_setting',
          onClick: () => navigate('/hr/leaves-settings') 
        },
        { 
          label: 'تنظیمات حضور غیاب', 
          icon: <ScheduleOutlined />,
          path: '/hr/attendance-settings',
          permission: 'manage_attendance_setting',
          onClick: () => navigate('/hr/attendance-settings') 
        },
      ],
    },
    {
      key: 'action',
      label: 'اقدام',
      icon: <ThunderboltOutlined />,
      color: 'from-yellow-500 to-yellow-600',
      // ماژول action نیازی به بررسی پرمیشن ندارد
      items: [
        { 
          label: 'داشبورد', 
          icon: <ThunderboltOutlined />,
          path: '/action/dashboard',
        },
        // { 
        //   label: 'صفحه اصلی', 
        //   icon: <ThunderboltOutlined />,
        //   path: '/action',
        // },
        { 
          label: 'اقدام‌های من', 
          icon: <UserOutlined />,
          path: '/action/my-actions',
        },
      ],
    },
    {
      key: 'settings',
      label: 'تنظیمات',
      icon: <SettingOutlined />,
      color: 'from-gray-500 to-gray-600',
      requiredPermission: 'manage_settings',
      items: [
        { 
          label: 'تنظیمات شرکت', 
          icon: <SettingOutlined />,
          path: '/settings/company',
          permission: 'manage_settings',
          onClick: () => navigate('/settings/company'),
        },
        { 
          label: 'نشانی کسب‌وکار', 
          icon: <ControlOutlined />,
          path: '/settings/business-address',
          permission: 'manage_settings',
          onClick: () => navigate('/settings/business-address'),
        },
        ...(isSuperAdmin
          ? [{
              label: 'بسته‌ها',
              icon: <AppstoreOutlined />,
              path: '/settings/packages',
              onClick: () => navigate('/settings/packages'),
            }]
          : []),
      ],
    },
    {
      key: 'reports',
      label: 'گزارشات',
      icon: <BarChartOutlined />,
      color: 'from-indigo-500 to-indigo-600',
      items: [
        { label: 'نمای کلی گزارشات', icon: <AppstoreOutlined />, path: '/reports', onClick: () => navigate('/reports') },
        { label: 'گزارش مالی', icon: <BarChartOutlined />, path: '/reports/finance', onClick: () => navigate('/reports/finance') },
        { label: 'درآمد در مقابل هزینه', icon: <BarChartOutlined />, path: '/reports/income-expense', onClick: () => navigate('/reports/income-expense') },
        { label: 'گزارش مرخصی', icon: <BarChartOutlined />, path: '/reports/leaves', onClick: () => navigate('/reports/leaves') },
        { label: 'سهمیه مرخصی کارکنان', icon: <BarChartOutlined />, path: '/reports/leave-quota', onClick: () => navigate('/reports/leave-quota') },
        { label: 'گزارش حضور و غیاب', icon: <BarChartOutlined />, path: '/reports/attendance', onClick: () => navigate('/reports/attendance') },
        { label: 'گزارش هزینه‌ها', icon: <BarChartOutlined />, path: '/reports/expenses', onClick: () => navigate('/reports/expenses') },
        { label: 'گزارش معاملات', icon: <BarChartOutlined />, path: '/reports/leads', onClick: () => navigate('/reports/leads') },
        { label: 'گزارش فروش', icon: <BarChartOutlined />, path: '/reports/sales', onClick: () => navigate('/reports/sales') },
      ],
    },
  ];

  const superAdminMenuConfig = useMemo(
    () => [
      {
        key: 'superadmin-dashboard',
        label: 'داشبورد',
        icon: <DashboardOutlined />,
        color: 'from-violet-500 to-purple-600',
        items: [
          {
            label: 'داشبورد سوپرادمین',
            icon: <DashboardOutlined />,
            path: '/dashboard/super-admin',
            onClick: () => navigate('/dashboard/super-admin'),
          },
        ],
      },
      {
        key: 'superadmin-packages',
        label: 'بسته‌ها',
        icon: <AppstoreOutlined />,
        color: 'from-violet-500 to-indigo-600',
        items: [
          {
            label: 'مدیریت بسته‌ها',
            icon: <AppstoreOutlined />,
            path: '/settings/packages',
            onClick: () => navigate('/settings/packages'),
          },
        ],
      },
      {
        key: 'superadmin-companies',
        label: 'شرکت‌ها',
        icon: <BankOutlined />,
        color: 'from-emerald-500 to-teal-600',
        items: [
          {
            label: 'لیست شرکت‌ها',
            icon: <UnorderedListOutlined />,
            path: '/settings/companies',
            onClick: () => navigate('/settings/companies'),
          },
          {
            label: 'شرکت جدید',
            icon: <PlusOutlined />,
            path: '/settings/companies/create',
            onClick: () => navigate('/settings/companies/create'),
          },
        ],
      },
      {
        key: 'superadmin-invoices',
        label: 'صورتحساب',
        icon: <SnippetsOutlined />,
        color: 'from-amber-500 to-orange-600',
        items: [
          {
            label: 'لیست صورتحساب‌ها',
            icon: <UnorderedListOutlined />,
            path: '/settings/superadmin-invoices',
            onClick: () => navigate('/settings/superadmin-invoices'),
          },
        ],
      },
      {
        key: 'superadmin-faqs',
        label: 'سوالات متداول مدیر',
        icon: <CustomerServiceOutlined />,
        color: 'from-indigo-500 to-violet-600',
        items: [
          {
            label: 'لیست سوالات',
            icon: <UnorderedListOutlined />,
            path: '/settings/superadmin-faqs',
            onClick: () => navigate('/settings/superadmin-faqs'),
          },
          {
            label: 'ایجاد سوال',
            icon: <PlusOutlined />,
            path: '/settings/superadmin-faqs/create',
            onClick: () => navigate('/settings/superadmin-faqs/create'),
          },
        ],
      },
      {
        key: 'superadmin-users',
        label: 'سوپر ادمین',
        icon: <CrownOutlined />,
        color: 'from-violet-500 to-fuchsia-600',
        items: [
          {
            label: 'لیست کاربران',
            icon: <UnorderedListOutlined />,
            path: '/settings/superadmin-users',
            onClick: () => navigate('/settings/superadmin-users'),
          },
          {
            label: 'کاربر جدید',
            icon: <PlusOutlined />,
            path: '/settings/superadmin-users/create',
            onClick: () => navigate('/settings/superadmin-users/create'),
          },
        ],
      },
      {
        key: 'support-tickets',
        label: 'تیکت پشتیبانی',
        icon: <CustomerServiceOutlined />,
        color: 'from-sky-500 to-cyan-600',
        items: [
          {
            label: 'لیست تیکت‌ها',
            icon: <UnorderedListOutlined />,
            path: '/settings/support-tickets',
            onClick: () => navigate('/settings/support-tickets'),
          },
          {
            label: 'تیکت جدید',
            icon: <PlusOutlined />,
            path: '/settings/support-tickets/create',
            onClick: () => navigate('/settings/support-tickets/create'),
          },
        ],
      },
      {
        key: 'superadmin-settings',
        label: 'تنظیمات',
        icon: <SettingOutlined />,
        color: 'from-slate-500 to-zinc-600',
        items: [
          {
            label: 'تنظیمات برنامه',
            icon: <AppstoreOutlined />,
            path: '/settings/superadmin/app-settings',
            onClick: () => navigate('/settings/superadmin/app-settings'),
          },
          {
            label: 'تنظیمات پروفایل',
            icon: <UserOutlined />,
            path: '/settings/superadmin/profile',
            onClick: () => navigate('/settings/superadmin/profile'),
          },
          {
            label: 'تنظیمات مالی',
            icon: <DollarOutlined />,
            path: '/settings/superadmin/invoice',
            onClick: () => navigate('/settings/superadmin/invoice'),
          },
          {
            label: 'تنظیمات زبان',
            icon: <TranslationOutlined />,
            path: '/settings/superadmin/language',
            onClick: () => navigate('/settings/superadmin/language'),
          },
          {
            label: 'تنظیمات ارز',
            icon: <DollarOutlined />,
            path: '/settings/superadmin/currency',
            onClick: () => navigate('/settings/superadmin/currency'),
          },
        ],
      },
    ],
    [navigate],
  );

  // فیلتر کردن منوها بر اساس پرمیشن‌ها
  const menuConfig = useMemo(() => {
    const moduleMenus = user?.moduleMenus;
    const usesLoginMenus =
      moduleMenus && typeof moduleMenus === 'object' && !Array.isArray(moduleMenus);

    if (isAdmin) {
      return allMenuConfig;
    }

    if (isSuperAdmin) {
      return superAdminMenuConfig;
    }

    const isModuleVisible = (menu) => {
      if (menu.key === 'action') return true;

      if (usesLoginMenus) {
        return isLoginModuleEnabled(menu.key, moduleMenus) === true;
      }

      if (menu.key === 'settings') return isAdmin;
      if (menu.module && !isModuleActive(menu.module)) return false;
      return true;
    };

    return allMenuConfig
      .filter((menu) => {
        if (!isModuleVisible(menu)) return false;

        if (usesLoginMenus) return true;

        if (menu.requiredPermissions?.length) {
          return menu.requiredPermissions.some((p) => canShowMenuItem(p));
        }
        if (menu.requiredPermission && !canShowMenuItem(menu.requiredPermission)) return false;
        return true;
      })
      .map((menu) => ({
        ...menu,
        items: menu.items.filter((item) => {
          if (usesLoginMenus) {
            return isLoginSubmenuAllowed(menu.key, item.path, moduleMenus);
          }
          if (item.module && !isModuleActive(item.module)) return false;
          if (item.permission && !canShowMenuItem(item.permission)) return false;
          return true;
        }),
      }))
      .filter((menu) => menu.items.length > 0);
  }, [
    isSuperAdmin,
    isAdmin,
    isModuleActive,
    canShowMenuItem,
    superAdminMenuConfig,
    allMenuConfig,
    user?.moduleMenus,
  ]);

  const handleIconClick = async (menu) => {
    // اگر ماژول action است، همیشه لاگین را صدا می‌زنیم تا توکن جدید بگیریم
    if (menu.key === 'action') {
      message.loading({ content: 'در حال ورود به اقدام...', key: 'actionLogin', duration: 0 });
      const result = await loginActionModule();
      message.destroy('actionLogin');
      
      if (result.success) {
        message.success(result.message || 'ورود با موفقیت انجام شد');
      } else {
        message.error(result.error || 'خطا در ورود به اقدام');
        return; // اگر لاگین ناموفق بود، ادامه نمی‌دهیم
      }
    }

    // اضافه کردن path به آیتم‌ها برای navigation
    const menuWithPaths = {
      ...menu,
      items: menu.items.map(item => ({
        ...item,
        path: item.onClick ? undefined : item.path,
        onClick: item.onClick || (item.path ? () => navigate(item.path) : undefined),
      })),
    };
    setActiveMenu(menuWithPaths);
    
    // پیدا کردن اولین آیتم که path دارد و navigate کردن به آن
    // اگر path نداشت، سعی می‌کنیم onClick را اجرا کنیم (اگر navigate کند)
    // برای action، همیشه به داشبورد navigate می‌کنیم
    if (menu.key === 'action') {
      navigate('/action/dashboard');
    } else {
    const firstItem = menu.items.find(item => item.path || item.onClick);
    if (firstItem) {
      if (firstItem.path) {
        navigate(firstItem.path);
      } else if (firstItem.onClick) {
        // اگر onClick وجود دارد، آن را اجرا می‌کنیم
        // (اگر onClick شامل navigate باشد، به آن صفحه می‌رود)
        firstItem.onClick();
        }
      }
    }
  };

  return (
    <MainLayout>
      {!activeMenu ? (
        <div className="w-full min-w-0 px-2 sm:px-0">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center dark:text-white" style={{ fontFamily: 'CustomFont' }}>
            خوش آمدید
          </h2>
          <p className="text-center text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 px-2" style={{ fontFamily: 'CustomFont' }}>
            برای شروع، یکی از بخش‌های زیر را انتخاب کنید:
          </p>
          <MenuIcons menuItems={menuConfig} onIconClick={handleIconClick} />
        </div>
      ) : (
        <div className="w-full min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-bold dark:text-white m-0" style={{ fontFamily: 'CustomFont' }}>
              {activeMenu.label}
            </h2>
            <button
              onClick={closeSidebar}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
              style={{ fontFamily: 'CustomFont' }}
            >
              بستن
            </button>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow min-w-0">
            <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base" style={{ fontFamily: 'CustomFont' }}>
              محتوای بخش {activeMenu.label} اینجا نمایش داده می‌شود.
            </p>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Dashboard;
